/**
 * Breadcrumb Component
 *
 * Displays a breadcrumb navigation trail with clickable items and separators.
 *
 * USAGE:
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', onClick: () => router.push('/') },
 *     { label: 'Services', onClick: () => router.push('/services') },
 *     { label: 'Cleaning', isActive: true }
 *   ]}
 *   textVariant="h2"
 *   iconSize="md"
 * />
 */

'use client';

import { type HTMLAttributes } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import type { IconName } from '@/src/shared/ui/icons/Icon';

export interface BreadcrumbItem {
  /**
   * Text label for the breadcrumb item
   */
  label: string;

  /**
   * Click handler for the breadcrumb item
   * If not provided, the item will not be clickable
   */
  onClick?: () => void;

  /**
   * Whether this is the active/current item
   * @default false
   */
  isActive?: boolean;

  /**
   * Custom weight for the text
   */
  weight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'extrabold';
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  /**
   * Array of breadcrumb items
   */
  items: BreadcrumbItem[];

  /**
   * Typography variant for breadcrumb text
   * @default 'h2'
   */
  textVariant?: 'h1' | 'h2' | 'h3' | 'body' | 'bodySm' | 'tag' | 'note';

  /**
   * Size of the separator icon
   * @default 'md'
   */
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;

  /**
   * Name of the separator icon
   * @default 'arrow-right'
   */
  separatorIcon?: IconName;

  /**
   * Gap between breadcrumb items
   * @default 'gap-2'
   */
  gap?: 'gap-1' | 'gap-2' | 'gap-3' | 'gap-4' | 'gap-6' | 'gap-8';

  /**
   * Shrink the label text on small screens (h1/h2 variants only) so long
   * titles don't get squeezed. Restores the design-system size from `sm` up.
   * @default false
   */
  shrinkOnMobile?: boolean;

  /**
   * Additional classes for the nav container
   */
  className?: string;
}

/**
 * Mobile font-size override per heading variant. Placed in the same `cn()` as the
 * variant size token so tailwind-merge resolves the base-size conflict reliably
 * (mobile value wins below `sm`, design-system size restored at `sm` and up).
 */
const MOBILE_SHRINK: Partial<Record<NonNullable<BreadcrumbProps['textVariant']>, string>> = {
  h1: '!text-[22px] !leading-tight sm:!text-[28px] sm:!leading-[1.2]',
  h2: '!text-[18px] !leading-tight sm:!text-[24px] sm:!leading-[30px]',
};

/**
 * Breadcrumb Component
 *
 * Displays breadcrumb navigation with customizable text size, icon size, and styling.
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  textVariant = 'h2',
  iconSize = 'md',
  separatorIcon = 'arrow-right',
  gap = 'gap-2',
  shrinkOnMobile = false,
  className,
  ...props
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  const labelClassName = shrinkOnMobile ? MOBILE_SHRINK[textVariant] : undefined;

  return (
    <nav
      className={cn('flex', gap, className)}
      aria-label="Breadcrumb"
      {...props}
    >
      {items.map((item, index) => (
        <div key={index} className={cn('flex items-center', gap)}>
          {/* Breadcrumb Item */}
          {item.onClick && !item.isActive ? (
            <button
              onClick={item.onClick}
              className="text-text-primary hover:text-accent-primary transition-colors text-left"
              aria-current={item.isActive ? 'page' : undefined}
            >
              <Typography
                variant={textVariant}
                weight={item.weight || 'medium'}
                className={labelClassName}
              >
                {item.label}
              </Typography>
            </button>
          ) : (
            <Typography
              variant={textVariant}
              weight={item.weight || (item.isActive ? 'bold' : 'medium')}
              color={item.isActive ? 'primary' : 'primary'}
              as="span"
              aria-current={item.isActive ? 'page' : undefined}
              className={labelClassName}
            >
              {item.label}
            </Typography>
          )}

          {/* Separator (not after last item) */}
          {index < items.length - 1 && (
            <Icon
              name={separatorIcon}
              size={iconSize}
              color="primary"
              className="self-center"
              aria-hidden="true"
            />
          )}
        </div>
      ))}
    </nav>
  );
};

Breadcrumb.displayName = 'Breadcrumb';
